import { useMemo } from 'react';
import endpoints from '@services/api/endpoints';
import useFetch from '@services/api/useFetch';
import { replaceUrlParams } from '@utils/string-util';
import type {
  ActiveSubscriptionItem,
  ActiveSubscriptionResponse,
  SaasProduct,
  SubscriptionCancel,
  SubscriptionCreate,
  SubscriptionCreateCustomer,
  SubscriptionCustomer,
  SubscriptionUpdate,
  SubscriptionUpdateAddons,
} from 'types/account/subscriptions';
import type { PaymentMethod } from 'types/checkout';

export const useSubscriptionApi = () => {
  const api = useFetch();
  return useMemo(() => {
    return {
      async addPaymentMethod(
        organizationId: string,
        paymentMethodId: string
      ): Promise<void> {
        return api.post(
          replaceUrlParams(endpoints.subscriptions.addPaymentMethod, {
            organizationId,
          }),
          { paymentMethodId }
        );
      },
      async cancelSubscription(
        organizationId: string,
        params: SubscriptionCancel
      ): Promise<ActiveSubscriptionItem> {
        return api.post(
          replaceUrlParams(endpoints.subscriptions.cancel, { organizationId }),
          params
        );
      },
      async createSubscriptionCustomer(
        params: SubscriptionCreateCustomer
      ): Promise<SubscriptionCustomer> {
        return api.post(endpoints.subscriptions.createCustomer, params);
      },
      async createSubscription(
        organizationId: string,
        params: SubscriptionCreate
      ): Promise<ActiveSubscriptionItem> {
        return api.post(
          replaceUrlParams(endpoints.subscriptions.create, { organizationId }),
          params
        );
      },
      async getActiveSubscriptions(
        organizationId: string
      ): Promise<ActiveSubscriptionResponse> {
        return api.get(
          replaceUrlParams(endpoints.subscriptions.active, { organizationId })
        );
      },
      async getPaymentMethods(
        organizationId: string
      ): Promise<PaymentMethod[]> {
        return api.get(
          replaceUrlParams(endpoints.subscriptions.getPaymentMethods, {
            organizationId,
          })
        );
      },
      async getSaaSProducts(): Promise<SaasProduct[]> {
        return api.get(endpoints.subscriptions.products);
      },
      async uncancelSubscription(
        organizationId: string,
        params: SubscriptionCancel
      ): Promise<ActiveSubscriptionItem> {
        return api.post(
          replaceUrlParams(endpoints.subscriptions.uncancel, {
            organizationId,
          }),
          params
        );
      },
      async updateDefaultPaymentMethod(
        organizationId: string,
        params: { paymentMethodId: string }
      ): Promise<void> {
        return api.put(
          replaceUrlParams(endpoints.subscriptions.updateDefaultPaymentMethod, {
            organizationId,
          }),
          params
        );
      },
      async updateSubscription(
        organizationId: string,
        params: SubscriptionUpdate
      ): Promise<ActiveSubscriptionItem> {
        return api.put(
          replaceUrlParams(endpoints.subscriptions.update, { organizationId }),
          params
        );
      },
      async updateSubscriptionAddons(
        organizationId: string,
        params: SubscriptionUpdateAddons
      ): Promise<ActiveSubscriptionItem> {
        return api.post(
          replaceUrlParams(endpoints.subscriptions.updateAddons, {
            organizationId,
          }),
          params
        );
      },
    };
  }, [api]);
};

export const subscriptionApiKeys = {
  all: ['subscription'] as const,
  getActiveSubscriptions: (organizationId: string) =>
    [
      ...subscriptionApiKeys.all,
      'active-subscriptions',
      organizationId,
    ] as const,
  getPaymentMethods: (organizationId: string) =>
    [...subscriptionApiKeys.all, 'payment-methods', organizationId] as const,
  getSubscriptionCustomer: (customerId: string) =>
    [...subscriptionApiKeys.all, 'subscription-customer', customerId] as const,
  getSaaSProducts: () => [...subscriptionApiKeys.all, 'subscriptions'] as const,
};
